import { create } from "zustand";
import { useEffect } from "react";

export type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "theme";

function resolveInitialTheme(): Theme {
  try {
    const stored = (localStorage.getItem(THEME_STORAGE_KEY) as Theme) || null;
    if (stored === "light" || stored === "dark") return stored;
  } catch {}

  const envDefault = (import.meta.env.VITE_DEFAULT_THEME as Theme) || undefined;
  if (envDefault === "light" || envDefault === "dark") return envDefault;

  if (typeof window !== "undefined" && window.matchMedia) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }

  return "dark";
}

function applyTheme(theme: Theme) {
  document.documentElement.setAttribute("data-theme", theme);
  // Tailwind dark mode uses the `dark` class on <html>.
  document.documentElement.classList.toggle("dark", theme === "dark");
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {}
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: "dark",
  setTheme: (theme) => {
    applyTheme(theme);
    set({ theme });
  },
  toggleTheme: () => {
    const next: Theme = get().theme === "dark" ? "light" : "dark";
    applyTheme(next);
    set({ theme: next });
  },
}));

let initialized = false;

/**
 * Synchronous bootstrap — call once during app bootstrap (in main.tsx, before
 * the first render) so the right `data-theme` is applied before paint.
 */
export function initThemeOnce() {
  if (initialized) return;
  initialized = true;

  const initial = resolveInitialTheme();
  applyTheme(initial);
  useThemeStore.setState({ theme: initial });

  // Cross-tab sync.
  window.addEventListener("storage", (e) => {
    if (
      e.key === THEME_STORAGE_KEY &&
      (e.newValue === "light" || e.newValue === "dark")
    ) {
      applyTheme(e.newValue);
      useThemeStore.setState({ theme: e.newValue });
    }
  });

  // Follow system preference only if user hasn't explicitly chosen.
  if (!localStorage.getItem(THEME_STORAGE_KEY)) {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", (e) => {
        const next: Theme = e.matches ? "dark" : "light";
        applyTheme(next);
        useThemeStore.setState({ theme: next });
      });
  }
}

/**
 * React hook — gives components a reactive view of the theme.
 */
export function useTheme() {
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  return {
    theme,
    isDark: theme === "dark",
    setTheme,
    toggleTheme,
  };
}

/**
 * Hook that mounts initThemeOnce on first use — convenient for components
 * that need to ensure the theme system is wired without touching main.tsx.
 */
export function useThemeInit() {
  useEffect(() => {
    initThemeOnce();
  }, []);
}
